/**
 * Font optimization utilities to prevent preload warnings
 * and improve Core Web Vitals scores
 */

/**
 * Optimize font loading to prevent unused preload warnings
 * This function should be called early in the app lifecycle
 */
function optimizeFontLoading() {
  // Only run on client side
  if (typeof window === 'undefined') return;

  // Remove unused font preloads that are causing warnings
  const preloadLinks = document.querySelectorAll('link[rel="preload"][as="font"]');
  preloadLinks.forEach((link) => {
    const href = link.getAttribute('href');
    if (href && !isFontActuallyUsed(href)) {
      // Convert preload to prefetch for unused fonts
      link.setAttribute('rel', 'prefetch');
      link.setAttribute('fetchpriority', 'low');
    }
  });

  // Set font-display: swap for better performance
  setFontDisplaySwap();
}

/**
 * Check if a font is actually used on the current page
 */
function isFontActuallyUsed(fontHref: string): boolean {
  // Check if the font file name matches our used fonts
  const usedFontPatterns = [
    /geist-sans.*-[0-9a-f]+\.woff2$/, // Geist Sans (used in body)
    /geist-mono.*-[0-9a-f]+\.woff2$/, // Geist Mono (used for code)
  ];

  return usedFontPatterns.some((pattern) => pattern.test(fontHref));
}

/**
 * Set font-display: swap for better font loading performance
 */
function setFontDisplaySwap() {
  // Inject CSS to set font-display: swap for all font faces
  const styleSheet = document.createElement('style');
  styleSheet.textContent = `
    @font-face {
      font-display: swap;
    }
    
    /* Optimize Geist fonts */
    .font-sans, .font-geist-sans {
      font-display: swap;
    }
    
    .font-mono, .font-geist-mono {
      font-display: swap;
    }
  `;

  document.head.appendChild(styleSheet);
}

/**
 * Remove unused resource preloads that are causing warnings
 */
function cleanupUnusedPreloads() {
  if (typeof window === 'undefined') return;

  // List of resources that are preloaded but might not be used immediately
  const potentiallyUnusedResources = [
    '/uic_logo.svg', // Only used in loading screen and header
  ];

  potentiallyUnusedResources.forEach((resource) => {
    const link = document.querySelector(`link[rel="preload"][href="${resource}"]`);
    if (link && !isResourceVisible(resource)) {
      // Convert to prefetch if not immediately visible
      link.setAttribute('rel', 'prefetch');
      link.setAttribute('fetchpriority', 'low');
    }
  });
}

/**
 * Check if a resource is immediately visible/used
 */
function isResourceVisible(resource: string): boolean {
  switch (resource) {
    case '/uic_logo.svg':
      // Check if logo is currently visible
      return !!document.querySelector('img[src="/uic_logo.svg"]');
    default:
      return false;
  }
}

/**
 * Initialize font optimization on page load
 */
export function initFontOptimization() {
  if (typeof window === 'undefined') return;

  // Run immediately
  optimizeFontLoading();

  // Run after initial paint to clean up unused preloads
  requestIdleCallback(
    () => {
      cleanupUnusedPreloads();
    },
    { timeout: 2000 }
  );

  // Run on page visibility change to handle tab switching
  document.addEventListener('visibilitychange', () => {
    if (!document.hidden) {
      cleanupUnusedPreloads();
    }
  });
}

/**
 * Polyfill for requestIdleCallback
 */
function requestIdleCallback(callback: () => void, options?: { timeout?: number }) {
  if ('requestIdleCallback' in window) {
    return window.requestIdleCallback(callback, options);
  }

  // Fallback for browsers without requestIdleCallback
  return setTimeout(callback, options?.timeout || 1000);
}

